"use client";
import { CalcRow, ChoicePills, RangeField } from "@/components/marketing";
import type { ChoiceOption } from "@/components/marketing";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import DemoPanel, {
  demoGridClass,
  demoHeadingClass,
} from "../demoPanel/demoPanel.component";
import { useProgrammePlanner } from "./hooks";

interface PlannerBusiness extends ChoiceOption {
  // The example reward for this kind of shop ("a free dessert").
  item: string;
  // The example spend goal ("₹3,000 in a month").
  goal: string;
}

interface PlannerGoal extends ChoiceOption {
  type: string;
  earn: string;
  limits: string;
  watch: string;
  // Template with {item} and {goal}.
  example: string;
}

export interface ProgrammePlannerCopy {
  businessHeading: string;
  businesses: PlannerBusiness[];
  goalHeading: string;
  goals: PlannerGoal[];
  costHeading: string;
  billLabel: string;
  marginLabel: string;
  marginAriaLabel: string;
  rateLabel: string;
  rateAriaLabel: string;
  resultHeading: string;
  resultLabels: {
    type: string;
    earn: string;
    example: string;
    limits: string;
    watch: string;
  };
  costLabel: string;
  marginValueLabel: string;
  shareLabel: string;
  barLabel: string;
  verdicts: { low: string; mid: string; high: string };
  initial: {
    business: string;
    goal: string;
    bill: number;
    margin: number;
    rate: number;
  };
}

interface ProgrammePlannerProps {
  copy: ProgrammePlannerCopy;
}

const dtClass =
  "mt-1.5 text-xs font-extrabold tracking-[0.07em] text-ink-muted uppercase";
const ddClass = "text-[15.5px] leading-[1.45] text-ink";

// The preview's `programme_planner`: pick a business and a goal for a
// starting programme, then check the reward against your own margin.
const ProgrammePlanner = ({ copy }: ProgrammePlannerProps) => {
  const planner = useProgrammePlanner(copy);
  const { state, goal } = planner;
  const results = [
    { label: copy.resultLabels.type, value: goal.type },
    { label: copy.resultLabels.earn, value: goal.earn },
    { label: copy.resultLabels.example, value: planner.example },
    { label: copy.resultLabels.limits, value: goal.limits },
    { label: copy.resultLabels.watch, value: goal.watch },
  ];
  return (
    <div className={demoGridClass}>
      <DemoPanel>
        <h3 className={demoHeadingClass}>{copy.businessHeading}</h3>
        <ChoicePills
          ariaLabel={copy.businessHeading}
          options={copy.businesses}
          value={state.business}
          onChange={planner.setBusiness}
        />
        <h3 className={demoHeadingClass}>{copy.goalHeading}</h3>
        <ChoicePills
          ariaLabel={copy.goalHeading}
          options={copy.goals}
          value={state.goal}
          onChange={planner.setGoal}
        />
        <h3 className={demoHeadingClass}>{copy.costHeading}</h3>
        <div className="grid gap-3">
          <div className="grid gap-2">
            <label
              htmlFor="planner-bill"
              className="text-[15px] font-semibold text-ink"
            >
              {copy.billLabel}
            </label>
            <Input
              id="planner-bill"
              type="number"
              inputMode="numeric"
              min={0}
              step={100}
              value={state.bill}
              onChange={(event) => planner.setBill(event.target.value)}
              className="h-auto rounded-[14px] border-[1.5px] border-line px-3.5 py-3 text-[22px] font-bold text-ink focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/30 md:text-[22px]"
            />
          </div>
          <RangeField
            id="planner-margin"
            label={copy.marginLabel}
            output={`${state.margin}%`}
            ariaLabel={copy.marginAriaLabel}
            min={5}
            max={40}
            step={1}
            value={state.margin}
            onChange={planner.setMargin}
          />
          <RangeField
            id="planner-rate"
            label={copy.rateLabel}
            output={`${state.rate}%`}
            ariaLabel={copy.rateAriaLabel}
            min={1}
            max={10}
            step={0.5}
            value={state.rate}
            onChange={planner.setRate}
          />
        </div>
      </DemoPanel>
      <DemoPanel isLive>
        <h3 className={demoHeadingClass}>{copy.resultHeading}</h3>
        <dl className="grid gap-1.5">
          {results.map((result) => (
            <div key={result.label} className="grid gap-1">
              <dt className={dtClass}>{result.label}</dt>
              <dd className={ddClass}>{result.value}</dd>
            </div>
          ))}
        </dl>
        <div className="grid gap-2 rounded-[20px] bg-cream px-4 py-3.5">
          <CalcRow label={copy.costLabel} value={planner.costText} />
          <CalcRow label={copy.marginValueLabel} value={planner.marginText} />
          <CalcRow label={copy.shareLabel} value={planner.shareText} isTotal />
          <Progress
            value={planner.barValue}
            aria-label={copy.barLabel}
            className="h-3 bg-line"
            indicatorClassName="bg-brand-gradient motion-reduce:transition-none"
          />
          <p className="text-[14.5px] leading-normal text-ink-muted">
            {planner.verdict}
          </p>
        </div>
      </DemoPanel>
    </div>
  );
};

export default ProgrammePlanner;
